import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLoyaltyStatistics } from "@/hooks/loyalty/useLoyaltyStatistics";
import { Users, Wallet, Activity, Gift, Loader2, Trophy } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';

interface LoyaltyStatisticsProps {
  businessId: string | null;
}

interface LoyaltyProgram {
  id: string;
  name: string;
  program_type: string;
}

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

const LoyaltyStatistics: React.FC<LoyaltyStatisticsProps> = ({ businessId }) => {
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  const { stats, loading, error } = useLoyaltyStatistics(businessId, selectedProgramId);

  // Fetch available programs
  useEffect(() => {
    const fetchPrograms = async () => {
      if (!businessId) return;

      setLoadingPrograms(true);
      try {
        const { data, error } = await supabase
          .from('loyalty_programs')
          .select('id, name, program_type')
          .eq('business_id', businessId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setPrograms(data || []);
      } catch (err) {
        console.error('Error fetching programs:', err);
      } finally {
        setLoadingPrograms(false);
      }
    };

    fetchPrograms();
  }, [businessId]);

  if (loadingPrograms || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load loyalty statistics: {error}
        </AlertDescription>
      </Alert>
    );
  }

  // Check if there's any data at all (passes or members)
  const hasData = stats.activePasses > 0 || stats.totalMembers > 0;

  // Calculate total activity (stamps + points)
  const totalActivity = stats.totalStamps + stats.totalPoints;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Loyalty Program Statistics</h2>
          <p className="text-muted-foreground mt-2">
            View analytics and insights about your loyalty program performance
          </p>
        </div>

        {programs.length > 0 && (
          <div className="w-64">
            <Select
              value={selectedProgramId || "all"}
              onValueChange={(value) => setSelectedProgramId(value === "all" ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {programs.length === 0 ? (
        <Alert>
          <AlertDescription>
            No loyalty programs found. Create a loyalty program to start tracking statistics.
          </AlertDescription>
        </Alert>
      ) : !hasData ? (
        <Alert>
          <AlertDescription>
            No data available yet. Start distributing passes and acquiring members to see statistics.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(stats.totalMembers)}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.activeRate.toFixed(1)}% active (last 30 days)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Active Passes</CardTitle>
                <Wallet className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(stats.activePasses)}</div>
                <p className="text-xs text-muted-foreground">
                  Apple: {formatNumber(stats.platformBreakdown.apple)} | Google: {formatNumber(stats.platformBreakdown.google)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Total Activity</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(totalActivity)}</div>
                <p className="text-xs text-muted-foreground">
                  {formatNumber(stats.totalStamps)} stamps | {formatNumber(stats.totalPoints)} points
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Redemptions</CardTitle>
                <Gift className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(stats.totalRedemptions)}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.redemptionRate.toFixed(1)}% redemption rate
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Customer Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                      <span className="text-sm">Active (last 30 days)</span>
                    </div>
                    <span className="text-sm font-medium">
                      {formatNumber(stats.customerSegmentation.active)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-yellow-500" />
                      <span className="text-sm">At Risk (30-90 days)</span>
                    </div>
                    <span className="text-sm font-medium">
                      {formatNumber(stats.customerSegmentation.atRisk)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="text-sm">Inactive (90+ days)</span>
                    </div>
                    <span className="text-sm font-medium">
                      {formatNumber(stats.customerSegmentation.inactive)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-gray-500" />
                      <span className="text-sm">Never Transacted</span>
                    </div>
                    <span className="text-sm font-medium">
                      {formatNumber(stats.customerSegmentation.neverTransacted)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Program Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Average Visits per Member</span>
                    <span className="text-sm font-medium">{stats.averageVisits.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Active Engagement Rate</span>
                    <span className="text-sm font-medium">{stats.activeRate.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Redemption Rate</span>
                    <span className="text-sm font-medium">{stats.redemptionRate.toFixed(1)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Member Growth Chart */}
            {stats.memberGrowth.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <CardTitle className="text-sm font-medium">Member Growth</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={stats.memberGrowth}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        className="text-xs"
                      />
                      <YAxis className="text-xs" />
                      <Tooltip
                        labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={{ fill: 'hsl(var(--primary))' }}
                        name="New Members"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Platform Distribution Pie Chart */}
            {stats.activePasses > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <CardTitle className="text-sm font-medium">Platform Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Apple Wallet', value: stats.platformBreakdown.apple },
                          { name: 'Google Wallet', value: stats.platformBreakdown.google }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#000000" />
                        <Cell fill="#4285f4" />
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Transaction Activity Chart */}
          {stats.transactionActivity.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Transaction Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.transactionActivity}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                    />
                    <Legend />
                    <Bar dataKey="earn_points" stackId="a" fill="#10b981" name="Points Earned" />
                    <Bar dataKey="earn_stamps" stackId="a" fill="#3b82f6" name="Stamps Earned" />
                    <Bar dataKey="redeem_points" stackId="b" fill="#f59e0b" name="Points Redeemed" />
                    <Bar dataKey="redeem_stamps" stackId="b" fill="#ef4444" name="Stamps Redeemed" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Top Customers Leaderboard */}
          {stats.topCustomers.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <CardTitle className="text-sm font-medium">Top Customers</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead className="text-right">Points</TableHead>
                      <TableHead className="text-right">Stamps</TableHead>
                      <TableHead className="text-right">Visits</TableHead>
                      <TableHead className="text-right">Last Visit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.topCustomers.map((customer, index) => (
                      <TableRow key={customer.customer_id}>
                        <TableCell className="font-medium">
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            {customer.first_name || customer.last_name ? (
                              <span className="font-medium">
                                {customer.first_name} {customer.last_name}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                Customer {customer.customer_id.slice(0, 8)}...
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            {customer.email && (
                              <span className="text-xs text-muted-foreground">{customer.email}</span>
                            )}
                            {customer.phone && (
                              <span className="text-xs text-muted-foreground">{customer.phone}</span>
                            )}
                            {!customer.email && !customer.phone && (
                              <span className="text-xs text-muted-foreground">No contact info</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {customer.current_points > 0 ? formatNumber(customer.current_points) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {customer.current_stamps > 0 ? formatNumber(customer.current_stamps) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatNumber(customer.total_visits)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {customer.last_transaction_date
                            ? new Date(customer.last_transaction_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'Never'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default LoyaltyStatistics;
